import { useState, useEffect, useCallback } from "react";
import { chatId, useAiContext } from "../providers/AiProvider";
import { useSelectedProject } from "Hooks";
import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import { AnimatePresence } from "motion/react";
import { Box, Typography, Link, Chip, Stack } from "@mui/material";
import { motion } from "motion/react";
import { ReactComponent as TrainIcon } from "Assets/icons/menu/train.svg";
import { OpenInNew } from "@mui/icons-material";
import AiTextField from "./AiTextField";

interface IAiChatProps {}

function AiChat({}: IAiChatProps) {
  const { selectedProject: project } = useSelectedProject();

  const [isExiting, setIsExiting] = useState(false);
  const {
    active,
    setActive,
    setCurrentQuestion,
    createSessionAndStream,
    clearCurrentSession,
  } = useAiContext();

  useEffect(() => {
    if (isExiting) {
      const exitTimer = setTimeout(() => {
        setActive(true);
      }, 1000); // 1 second for exit animation

      return () => clearTimeout(exitTimer);
    }
  }, [isExiting]);

  useEffect(() => {
    if (active) {
      setIsExiting(false);
      return;
    }
    const id = new URLSearchParams(window.location.search).get(chatId);
    if (!id) {
      clearCurrentSession();
    }
  }, [active]);

  const selectCurrentQuestion = useCallback(
    (question: string) => {
      clearCurrentSession();
      setCurrentQuestion(question);
      createSessionAndStream(question);
      setIsExiting(true);
    },
    [setCurrentQuestion, clearCurrentSession],
  );

  const promptExamples = [
    "Show me the top landing pages by traffic & engagement for Organic Search",
    "Show me my add to cart rate over the last 30 days",
    "How does conversion rate compare by device",
    "Show me core web vitals performance over the last 3 months",
  ];

  return (
    <Box>
      <CenterPage calcHeight sm={10}>
        {!project ? (
          <Loading fullHeight />
        ) : (
          <AnimatePresence>
            {!isExiting && (
              <motion.div
                key="main-content"
                initial={{ opacity: 1, scale: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  y: -20,
                  transition: { duration: 0.8, ease: "easeInOut" },
                }}
                transition={{ duration: 0.3 }}
              >
                <Stack alignItems="center" spacing={2} sx={{ width: 700 }}>
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: "easeOut",
                      type: "spring",
                      stiffness: 100,
                    }}
                  >
                    <Box sx={{ width: 60, height: 60 }}>
                      <TrainIcon style={{ width: "100%", height: "100%" }} />
                    </Box>
                  </motion.div>
                  <Typography variant="h1">Hi, how can I help?</Typography>
                  <Typography variant="body1" mb={4}>
                    Need help getting started?{" "}
                    <Link href="https://www.incendium.ai/" target="_blank">
                      View quick start guide <OpenInNew fontSize="inherit" />
                    </Link>
                  </Typography>
                  <AiTextField
                    onSubmit={() => {
                      setIsExiting(true);
                    }}
                  />
                  <Stack
                    useFlexGap
                    direction="row"
                    spacing={2}
                    sx={{ maxWidth: 600, flexWrap: "wrap" }}
                  >
                    {promptExamples.map((example, index) => (
                      <motion.div
                        key={example}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: index * 0.1,
                          ease: "easeOut",
                        }}
                      >
                        <Chip
                          label={example}
                          color="default"
                          variant="outlined"
                          onClick={() => {
                            selectCurrentQuestion(example);
                          }}
                        />
                      </motion.div>
                    ))}
                  </Stack>
                </Stack>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </CenterPage>
    </Box>
  );
}

export default AiChat;
